import { useEffect, useState } from 'react'
import type { PostHog } from 'posthog-js'

import { useAuthState } from '@percent/cause-dashboard/common/hooks/useAuthState/useAuthState'
import { getBackendFlagsClient } from './backendFlagsClient'
import type { BackendPostHogFeatureFlag } from './backendPosthogFeatureFlags'

export type BackendFlagResult<T> = {
  // false until flags have loaded for the logged-in user; gate on this before rendering
  ready: boolean
  value: T
}

type FlagKind = 'boolean' | 'variant'

type FlagValue<K extends FlagKind> = K extends 'boolean' ? boolean : string | boolean | undefined

// Each read sends $feature_flag_called to the backend project, so a flag's Usage tab shows who read it and its value
const flagReadOptions = { send_event: true }

const fallbackFor = <K extends FlagKind>(kind: K) => (kind === 'boolean' ? false : undefined) as FlagValue<K>

const readFlag = <K extends FlagKind>(client: PostHog, key: BackendPostHogFeatureFlag, kind: K) =>
  (kind === 'boolean'
    ? client.isFeatureEnabled(key, flagReadOptions) === true
    : client.getFeatureFlag(key, flagReadOptions)) as FlagValue<K>

// With no backend project key, ready is true with the fallback, so gated UI stays off rather than waiting forever
const useBackendFlag = <K extends FlagKind>(
  key: BackendPostHogFeatureFlag,
  kind: K
): BackendFlagResult<FlagValue<K>> => {
  const { authState } = useAuthState()
  const accountId = authState.user?.id
  const [result, setResult] = useState<(BackendFlagResult<FlagValue<K>> & { accountId?: string }) | undefined>()

  useEffect(() => {
    if (!accountId) {
      return undefined
    }

    const client = getBackendFlagsClient(accountId)

    if (!client) {
      setResult({ accountId, ready: true, value: fallbackFor(kind) })

      return undefined
    }

    return client.onFeatureFlags(() => {
      if (client.get_distinct_id() !== accountId) {
        return
      }

      setResult({ accountId, ready: true, value: readFlag(client, key, kind) })
    })
  }, [accountId, key, kind])

  // Logged out, still loading, or the account just changed and its flags have not arrived yet
  if (!accountId || result?.accountId !== accountId) {
    return { ready: false, value: fallbackFor(kind) }
  }

  return { ready: result.ready, value: result.value }
}

export const useBackendFeatureFlag = (key: BackendPostHogFeatureFlag): BackendFlagResult<boolean> =>
  useBackendFlag(key, 'boolean')

export const useBackendFeatureFlagVariant = (
  key: BackendPostHogFeatureFlag
): BackendFlagResult<string | boolean | undefined> => useBackendFlag(key, 'variant')
